import { useState, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import classNames from 'classnames'

import classes from './PasswordChangeForm.module.scss'
import PasswordInputWithIcon from '../../common/form/PasswordInputWithIcon'
import { changePassword } from '../../../actions/userActions'
import { flatServerFormErrors } from '../../../utility/helperFunctions'
import { AccountContext } from '../AccountContext'

const CHANGE_PASSWORD_URL = `${process.env.REACT_APP_OAUTH_PROVIDER_HOST}/profile/change_password`

// eslint-disable-next-line complexity
const PasswordChangeForm = () => {
  const dispatch = useDispatch()
  const { user } = useContext(AccountContext)
  const currentUser = useSelector(state => state.user.current_user)

  const [isUpdating, setIsUpdating] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [errors, setErrors] = useState({})

  const isCurrentUser = user.id === currentUser.id
  const isFamilyAdmin = currentUser.type === 'Student' && currentUser.family_admin
  const familyAdminEditingJunior = isFamilyAdmin && !isCurrentUser && !user.family_admin

  const validateForm = formData => {
    setErrors({})
    const formErrors = {}

    const currentPassword = formData.get('currentPassword')
    const newPassword = formData.get('newPassword')
    const confirmPassword = formData.get('confirmPassword')

    if (!currentPassword) {
      formErrors['current_password'] = 'This field cannot be empty.'
    }

    if (!newPassword) {
      formErrors['new_password'] = 'This field cannot be empty.'
    }

    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      formErrors['confirm_password'] = 'Confirm Password does not match with New Password.'
    }

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors)
      return false
    }

    return true
  }

  const handleSubmitForm = async e => {
    e.preventDefault()
    const formElement = e.target
    const formData = new FormData(formElement)

    if (validateForm(formData)) {
      const passwordData = {
        current_password: formData.get('currentPassword'),
        password: formData.get('newPassword'),
        password_confirmation: formData.get('confirmPassword'),
      }

      try {
        setIsUpdating(true)
        const result = await dispatch(changePassword(user.id, passwordData))

        if (result.type === 'UI_ERROR') {
          if (result.error?.general) {
            toast.error(result.error.general.join(', '))
          } else {
            setErrors(flatServerFormErrors(result?.error))
          }
        } else {
          toast.success('Password has been successfully updated!')
          formElement.reset()
        }
      } catch {
        toast.error('Could not update password. Please try again later.')
      } finally {
        setIsUpdating(false)
        setHasChanges(false)
      }
    }
  }

  const handleDetectChanges = (e) => {
    const formData = new FormData(e.currentTarget)
    const hasChanges_ = !!(
      formData.get('currentPassword') ||
      formData.get('newPassword') ||
      formData.get('confirmPassword')
    )

    if (hasChanges_ !== hasChanges) {
      setHasChanges(hasChanges_)
    }
  }

  return (
    <div className={classNames(classes.container, 'animated fadeIn')}>
      <div className='row mb-3'>
        <h4 className={classes.title}>Change Password</h4>
      </div>

      {familyAdminEditingJunior ? (
        <form onSubmit={handleSubmitForm} onInput={handleDetectChanges}>
          <div className='row mb-3'>
            <label htmlFor='currentPassword'>Current Password</label>
            <PasswordInputWithIcon
              id='currentPassword'
              name='currentPassword'
              placeholder='Enter Current Password'
              classNames={{ inputContainer: classes.accountInputContainer }}
              error={errors?.current_password}
            />
          </div>
          <div className='row mb-3'>
            <label htmlFor='newPassword'>New Password</label>
            <PasswordInputWithIcon
              id='newPassword'
              name='newPassword'
              placeholder='Enter New Password'
              classNames={{ inputContainer: classes.accountInputContainer }}
              error={errors?.new_password}
            />
          </div>
          <div className={classNames(classes.extraMargin, 'row')}>
            <label htmlFor='confirmPassword'>Confirm Password</label>
            <PasswordInputWithIcon
              id='confirmPassword'
              name='confirmPassword'
              placeholder='Enter Confirm Password'
              classNames={{ inputContainer: classes.accountInputContainer }}
              error={errors?.confirm_password}
            />
          </div>
          <div className='row'>
            <button type='submit' className='btn btn-primary' disabled={isUpdating || !hasChanges}>
              Sav{isUpdating ? 'ing' : 'e'} Changes
            </button>
          </div>
        </form>
      ) : (
        <div className='row'>
          <p className={classes.infoText}>
            To change password,&nbsp;
            <a href={CHANGE_PASSWORD_URL} target='_blank' rel='noopener noreferrer'>click here</a>.
          </p>
        </div>
      )}
    </div>
  )
}

export default PasswordChangeForm
